import React, { useEffect } from "react";
import { useEvent } from "../../hooks/useEvent";
import { ANALYTICS_EVENTS } from "../../Constants/analyticsEvents";
import { stopLenis, startLenis } from "../../lib/lenis";

export default function ProjectModal({ project, onClose }) {
  const track = useEvent();

  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Lock background scroll while the modal is open - without this, scroll
  // input reaches the page behind the modal instead of the modal's own
  // content. Lenis drives scrolling independently of native `overflow`,
  // so it has to be paused separately from the body overflow toggle.
  useEffect(() => {
    if (!project) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    stopLenis();
    return () => {
      document.body.style.overflow = overflow;
      startLenis();
    };
  }, [project]);

  if (!project) return null;

  // Handle click outside modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-75 p-4"
      onClick={handleBackdropClick}
    >
      <div className="glass-card w-full max-w-2xl max-h-[80vh] overflow-y-auto overscroll-contain p-6 mx-4 rounded-lg border border-biolum-green/30">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-2xl font-bold">{project.title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white text-2xl leading-none"
          >
            ✕
          </button>
        </div>
        {project.link ? (
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block mb-4"
          >
            <div className="w-full h-[250px] bg-neutral-900 rounded-lg flex items-center justify-center overflow-hidden">
              <img
                src={project.image}
                alt={project.title}
                className="max-w-full max-h-full object-contain hover:opacity-90 transition-opacity"
              />
            </div>
          </a>
        ) : (
          <div className="w-full h-[250px] bg-neutral-900 rounded-lg flex items-center justify-center overflow-hidden mb-4">
            <img
              src={project.image}
              alt={project.title}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
        <p className="text-neutral-300 mb-4 text-pretty">{project.description}</p>
        <div className="flex flex-wrap gap-2">
          {project.technologies.map((tech, index) => (
            <span
              key={index}
              className="bg-neutral-900 px-2 py-1 text-sm font-medium text-biolum-green rounded"
            >
              {tech}
            </span>
          ))}
        </div>
        {project.link && (
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:bg-biolum-green hover:text-ocean-dark inline-block px-4 py-2 mt-4 transition-colors bg-biolum-blue text-ocean-dark font-bold rounded-lg shadow-[0_0_10px_rgba(0,255,255,0.3)] ripple-effect"
            onClick={() => track(ANALYTICS_EVENTS.VIEW_PROJECT_CLICK, { project: project.title })}
          >
            View Project
          </a>
        )}
      </div>
    </div>
  );
}
